import * as Sentry from '@sentry/react';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import getOmsUrl from '@utils/getOmsUrl';

interface ErrorCallback {
  (error: any): void;
}

export default class Checkout {
  private readonly omsUrl?: string;

  private readonly idToken: string;

  private readonly errorCallback: ErrorCallback;

  constructor(idToken: string, errorCallback: ErrorCallback) {
    if (!idToken) {
      throw new Error('idToken is required.');
    }
    this.omsUrl = getOmsUrl();
    this.idToken = idToken;
    this.errorCallback = (error) => errorCallback(error);
  }

  private handleError(error: any) {
    console.debug('Error: ', JSON.stringify(error));
    Sentry.captureException(error);
    this.errorCallback(error);
  }

  private getAxiosConfig(): AxiosRequestConfig {
    return {
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        token: this.idToken
      }
    };
  }

  public async createOrder(body: any, isPreviewOrder?: (order: any) => void): Promise<any> {
    const url = isPreviewOrder ? `${this.omsUrl}/orders/preview` : `${this.omsUrl}/orders`;
    try {
      const response = await axios.post(url, body, this.getAxiosConfig());

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const { response } = error as AxiosError;
        if (response?.data?.error) {
          const errorMessage = JSON.stringify(response.data.error);
          console.error('Checkout Error: ', errorMessage);
          throw new Error(errorMessage);
        }
      }
      this.handleError(error);
      throw new Error('An error occurred while making the API request.');
    }
  }

  public async getOrderById(orderId: string): Promise<any> {
    const url = `${this.omsUrl}/orders/${orderId}`;
    try {
      const response = await axios.get(url, this.getAxiosConfig());
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const { response } = error as AxiosError;
        if (response?.data?.error) {
          const errorMessage = JSON.stringify(response.data.error);
          console.error('Get Order Error: ', errorMessage);
          throw new Error(errorMessage);
        }
      }
      this.handleError(error);
      throw new Error('An error occurred while fetching the order.');
    }
  }
}
